import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../core/auth';
import { useNavigate } from '../core/rotas';
import { supabase } from '../core/supabase';
import { Button, EmptyState } from '../ui/basicos';
import { Icone } from '../ui/icones';
import { DialogoDoTopo } from './dialogo-do-topo';

/**
 * O sino do topo (o de YardHub, `ws-notification-launch`).
 *
 * `public.notifications` existe desde a 0008 e as automações do CRM e do
 * Marketing já gravam nela ("Notificar responsável", 0109, 0119, 0124 e a
 * função automations-run), mas nada no painel mostrava: o aviso ia para o
 * banco e ficava lá. Aqui a pessoa vê as dela - as endereçadas a ela e as da
 * organização inteira (`user_id` nulo) - abre o link, marca como lidas e
 * remove. A RLS já limita à organização; o filtro por pessoa é desta tela.
 *
 * Chega na hora pelo realtime (INSERT com o `user_id` dela) e, de reserva,
 * a cada minuto enquanto a aba está à vista.
 */

type Notificacao = { id: string; title: string; body: string; link: string | null; read_at: string | null; created_at: string; user_id: string | null };

const CHAVE = 'notificacoes-do-topo';

export function Notificacoes() {
  const { org, user, status } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [falha, setFalha] = useState('');
  const chave = [CHAVE, org?.id ?? null, user?.id ?? null];

  const q = useQuery<Notificacao[], Error>({
    queryKey: chave,
    enabled: status === 'ready' && !!org?.id && !!user?.id && !!supabase,
    staleTime: 30_000,
    refetchInterval: () => (typeof document !== 'undefined' && document.visibilityState === 'visible' ? 60_000 : false),
    queryFn: async () => {
      const { data, error } = await supabase!
        .from('notifications')
        .select('id,title,body,link,read_at,created_at,user_id')
        .eq('org_id', org!.id)
        .or(`user_id.eq.${user!.id},user_id.is.null`)
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw new Error('Não foi possível carregar suas notificações.');
      return (data ?? []) as Notificacao[];
    },
  });

  // Aviso novo entra sem esperar o minuto: o mesmo canal que o YardHub usa.
  useEffect(() => {
    if (!supabase || !user?.id || !org?.id) return;
    const sb = supabase;
    const canal = sb
      .channel(`notificacoes:${user.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` }, () => {
        void qc.invalidateQueries({ queryKey: [CHAVE] });
      })
      .subscribe();
    return () => { void sb.removeChannel(canal); };
  }, [user?.id, org?.id, qc]);

  const lista = q.data ?? [];
  const naoLidas = lista.filter((n) => !n.read_at);

  async function marcarComoLidas() {
    if (ocupado || naoLidas.length === 0 || !supabase) return;
    setOcupado(true);
    setFalha('');
    const { error } = await supabase.from('notifications').update({ read_at: new Date().toISOString() }).in('id', naoLidas.map((n) => n.id));
    if (error) setFalha('Não foi possível marcar como lidas. Tente de novo.');
    else qc.setQueryData<Notificacao[]>(chave, (antes) => (antes ?? []).map((n) => (n.read_at ? n : { ...n, read_at: new Date().toISOString() })));
    setOcupado(false);
  }

  async function remover(id: string) {
    if (ocupado || !supabase) return;
    setOcupado(true);
    setFalha('');
    const { error } = await supabase.from('notifications').delete().eq('id', id);
    if (error) setFalha('Não foi possível remover esta notificação.');
    else qc.setQueryData<Notificacao[]>(chave, (antes) => (antes ?? []).filter((n) => n.id !== id));
    setOcupado(false);
  }

  async function abrir(n: Notificacao) {
    if (!n.link) return;
    setAberto(false);
    // Abrir é ler: marca esta, sem esperar, e segue para o lugar dela.
    if (!n.read_at && supabase) {
      void supabase.from('notifications').update({ read_at: new Date().toISOString() }).eq('id', n.id)
        .then(() => qc.invalidateQueries({ queryKey: [CHAVE] }));
    }
    if (/^https?:\/\//.test(n.link)) window.open(n.link, '_blank', 'noopener,noreferrer');
    else navigate(n.link);
  }

  const quando = (iso: string) =>
    new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });

  return (
    <>
      <button
        type="button"
        className="topo-sino"
        aria-label={naoLidas.length ? `Notificações, ${naoLidas.length} não lidas` : 'Notificações'}
        aria-haspopup="dialog"
        onClick={() => setAberto(true)}
      >
        <Icone.Sino tamanho={19} aria-hidden="true" />
        {naoLidas.length > 0 && <span className="topo-sino__ponto" aria-hidden="true" />}
      </button>
      <DialogoDoTopo
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        olho={org?.name ?? 'Seu Suite'}
        titulo="Suas notificações."
        frase={naoLidas.length
          ? `${naoLidas.length} ${naoLidas.length === 1 ? 'novidade para acompanhar' : 'novidades para acompanhar'}.`
          : 'Os últimos avisos das automações e da equipe.'}
        className="notificacoes"
      >
        {q.isError ? (
          /* O vazio por erro do kit: a mensagem crua do banco ("JWT expired")
             não dizia nada a quem lê, e o "tentar de novo" era um link solto. */
          <EmptyState
            tipo="erro"
            title="Não deu para carregar as notificações."
            description="Pode ser a conexão. Tente de novo em instantes."
            action={<Button variant="secondary" size="sm" loading={q.isFetching} onClick={() => void q.refetch()}>Tentar de novo</Button>}
          />
        ) : q.isPending ? (
          <div className="notificacoes__esqueleto" aria-label="Carregando as notificações">
            {[0, 1, 2].map((k) => <span key={k} className="esqueleto" />)}
          </div>
        ) : lista.length === 0 ? (
          <div className="notificacoes__vazio">
            <span aria-hidden="true"><Icone.Sino tamanho={20} /></span>
            <h4>Tudo em dia por aqui.</h4>
            <p>Quando uma automação ou alguém da equipe avisar você, aparece neste espaço.</p>
          </div>
        ) : (
          <ul className="notificacoes__lista">
            {lista.map((n) => (
              <li key={n.id} className={n.read_at ? '' : 'nova'}>
                <button type="button" className="notificacoes__corpo" disabled={!n.link} onClick={() => void abrir(n)}>
                  <span className="notificacoes__simbolo" aria-hidden="true">
                    {n.read_at ? <Icone.CheckCirculo tamanho={15} /> : <Icone.Sino tamanho={15} />}
                  </span>
                  <span className="notificacoes__txt">
                    <b>{n.title}</b>
                    {n.body && <small>{n.body}</small>}
                    <time dateTime={n.created_at}>{quando(n.created_at)}</time>
                  </span>
                </button>
                <button type="button" className="notificacoes__remover" disabled={ocupado} aria-label={`Remover notificação: ${n.title}`} onClick={() => void remover(n.id)}>
                  <Icone.Fechar tamanho={14} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {falha && <p className="busca-global__aviso" role="alert">{falha}</p>}
        {naoLidas.length > 0 && (
          <div className="notificacoes__acoes">
            <Button variant="secondary" size="sm" loading={ocupado} onClick={() => void marcarComoLidas()}>
              Marcar como lidas
            </Button>
          </div>
        )}
      </DialogoDoTopo>
    </>
  );
}
